import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { UnpublishedListing } from '#/components/UnpublishedListing/UnpublishedListing';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiOrg } from '#/modules/listing/orgs/api/types/ApiOrg';
import type { ApiUnpublishedOrg } from '#/modules/listing/orgs/api/types/ApiUnpublishedOrg';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getParsedSearchQuery } from '#/modules/search/helpers/getParsedSearchQuery';
import { searchGetOrgsRoutePath } from '#/modules/search/orgs/routing/searchGetOrgsRoutePath';
import type { SearchPageLocationState } from '#/modules/search/pages/Search/types/SearchPageLocationState';

type Props = {
  unpublishedOrg: ApiOrg | ApiUnpublishedOrg;
};

export function UnpublishedOrgPage({ unpublishedOrg }: Props) {
  const { i18n, locale } = useI18n();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: unpublishedOrg.id,
  });

  const navigate = useNavigate();
  const search = useCallback(
    (query: string) => {
      const { parsedQuery, remoteKeywords } = getParsedSearchQuery({
        query,
        locale,
      });

      void navigate(searchGetOrgsRoutePath({ locale, q: parsedQuery }), {
        state: {
          searchFrom: 'Unpublished Org Page',
          remoteKeywords,
        } satisfies SearchPageLocationState,
      });
    },
    [locale, navigate],
  );

  return (
    <UnpublishedListing
      searchContext={searchContext}
      footerTitle={i18n('Search Orgs')}
      seeMoreText={i18n(
        'Search below for an organization or cause that interests you.',
      )}
      searchFormAnalyticsTitle="[main] View Org Page (Unpublished) - search"
      onSubmit={({ values }) => search(values.query)}
    />
  );
}
